import { TagIcon } from "@heroicons/react/24/outline";
import { REFERRAL_TIER_UNSET } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { SearchbarItemBadge } from "components/community-search/search-bar/searchbar-item-badge";
import { tierColorForToken } from "components/references/show-reference/tier-colors";
import { trpc } from "components/rpc/trpc";
import { Callout } from "components/ui/callout";
import { Checkbox } from "components/ui/checkbox";
import { defaultLinkStyle } from "components/ui/link";
import React from "react";
import { useFeature } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";

type Tier = inferProcedureOutput<AppRouter["referralTier"]["tiers"]>[number];

interface ReferralTierFilterProps {
    value: string[];
    onChange: (referralTierIds: string[]) => void;
}

const TierBadge: React.FC<{ name: string; color: string | null }> = props => {
    const color = tierColorForToken(props.color);
    return (
        <span
            className={`inline-block flex-shrink-0 rounded-full ${color.bg} ${color.text} px-3 py-0.5 text-sm font-medium`}>
            {props.name}
        </span>
    );
};

export const ReferralTierFilter: React.FC<ReferralTierFilterProps> = props => {
    const { onChange } = props;
    const value = props.value ?? [];

    const featureEnabled = useFeature("referralTiers");
    const { settings } = useTenantSettings();
    const enabled = featureEnabled && !!settings.referralTiersEnabled;

    const trpcClient = trpc.useTRPC();
    const tiersQuery = useQuery({
        ...trpcClient.referralTier.tiers.queryOptions(),
        enabled,
    });
    const tiers: Tier[] = (tiersQuery.data ?? []).filter(t => !t.isArchived);

    const toggle = (id: string, checked: boolean) => {
        if (checked) {
            if (!value.includes(id)) {
                onChange([...value, id]);
            }
        } else {
            onChange(value.filter(v => v !== id));
        }
    };

    if (!enabled) {
        return null;
    }

    return (
        <Callout
            align="left"
            renderButton={() => (
                <div className="flex items-center space-x-1 cursor-pointer text-gray-700 lg:whitespace-nowrap">
                    <TagIcon className="w-5 h-5 flex-shrink-0 mr-2" />
                    <span className="inline md:inline text-sm">Referral tier</span>
                    {value.length > 0 ? (
                        <SearchbarItemBadge counter={value.length} />
                    ) : null}
                </div>
            )}>
            <div className="w-56 space-y-2">
                {tiers.map(tier => (
                    <Checkbox
                        key={tier.id}
                        checked={value.includes(tier.id)}
                        onChange={e => toggle(tier.id, e.target.checked)}
                        label={<TierBadge name={tier.name} color={tier.color} />}
                    />
                ))}
                <Checkbox
                    checked={value.includes(REFERRAL_TIER_UNSET)}
                    onChange={e => toggle(REFERRAL_TIER_UNSET, e.target.checked)}
                    label="No tier"
                />
                {value.length > 0 ? (
                    <a
                        href="#"
                        className={`${defaultLinkStyle} block`}
                        onClick={e => {
                            e.preventDefault();
                            onChange([]);
                        }}>
                        Clear
                    </a>
                ) : null}
            </div>
        </Callout>
    );
};
